import { Component, Input, OnChanges, SimpleChanges } from "@angular/core"
import { MaterialModule } from "../material/material.module"
import { FormsModule } from "@angular/forms"
import { Linqset, LINQSET_TYPE } from "../common/linqset-model"
import { AIServices } from "./ai-services"
import { MatSnackBar } from "@angular/material/snack-bar"
import { AIPrompt, createModelSummary, ModelSummary } from "../common/organisation"
import { DomSanitizer, SafeHtml } from "@angular/platform-browser"
import { DownloadService } from "../model-services/download.service"
import { cleanSummaryHtml } from "../linq-utils/formatting/html"
import { USER_ROLE } from "../common/user"


@Component({
    selector: 'app-query-ai',
    styleUrls: ['./query-ai.css'],
    templateUrl: 'query-ai.html',
    standalone: true,
    imports: [MaterialModule, FormsModule]
})
export class QueryAIComponent implements OnChanges {
    @Input() linqset:Linqset

    loading:boolean
    summaries:ModelSummary[] = []
    prompts:AIPrompt[] = []
    allPrompts:AIPrompt[] = []

    compare:boolean = false
    selectedPrompt:AIPrompt
    summary:ModelSummary
    safeHtml:SafeHtml
    style:string
    message:string
    format:string="html"
    description:string

    LINQSET_TYPE= LINQSET_TYPE

    constructor(private aiServices:AIServices, private snack:MatSnackBar, private sanitizer: DomSanitizer, private downloadService:DownloadService) {

    }

    async ngOnChanges(changes: SimpleChanges): Promise<void> {
        if (this.linqset) {
            if (this.allPrompts.length === 0) this.allPrompts = await this.aiServices.getPrompts(this.linqset.organisationKey)
            if (changes['linqset']) this.getSummaries()
        }
    }

    onSummaryChange(v:ModelSummary) {
        this.summary = v
        const start = v.htmlSummary.indexOf("<style>")
        // constrain any styling to this html only
        if (start > -1) {
            const end = v.htmlSummary.indexOf("</style>")
            const style = `<style>@scope (.my-scoped-section) { ${v.htmlSummary.substring(start+7, end)}} </style>`
            const html = `<div class="my-scoped-section">${v.htmlSummary.substring(0, start) + v.htmlSummary.substring(end+8)}</div>`

            this.format = "html"

            this.safeHtml = this.sanitizer.bypassSecurityTrustHtml(cleanSummaryHtml(style+html))
        } else {
            this.safeHtml = this.sanitizer.bypassSecurityTrustHtml(cleanSummaryHtml(v.htmlSummary))

            if (v.htmlSummary.indexOf('<!DOCTYPE html>') > -1) {
                this.format = "html"
            }else this.format = "text"
        }
        this.message = this.getMessage(v)
        this.setDescription()
    }

    onPromptChange(v:AIPrompt) {
        this.summary.prompt = v.prompt
        this.linqset.filter.comparison = v.comparison
    }

    async getSummaries() {
        this.prompts = this.allPrompts.filter(p => this.linqset.isProjectModel() || !p.comparison)

        this.summaries = await this.aiServices.getModelSummaries(this.linqset)
        this.onNew()
    }

    async onGenerate() {
        this.loading = true
        try {
            const s = await this.aiServices.generateModelSummary(this.linqset, this.summary.prompt)
            if (s) {
                this.summary.htmlSummary = s
                this.onSummaryChange(this.summary)
            }
        }catch(err) {
            console.error(err)
            this.snack.open("Error while loading summary", "close")
        }finally {
            this.loading = false
        }
    }

    onNew() {
        this.summary = createModelSummary(this.linqset.organisationKey, "", "", "", this.linqset.modelKey, this.linqset.workspaceKey, this.linqset.filter?._key)
        this.selectedPrompt = undefined
        this.setDescription()
    }

    async onSave() {
        try {
            await this.aiServices.saveModelSummary(this.linqset, this.summary)
            this.summaries = await this.aiServices.getModelSummaries(this.linqset)
            this.snack.open("Summary has been saved", "close")
        }catch(err) {
            console.error(err)
            this.snack.open("Error while saving summary", "close")
        }finally {
            this.loading = false
        }
    }

    async onDelete() {
        try {
            await this.aiServices.deleteModelSummary(this.linqset, this.summary)
            this.summaries = this.summaries.filter(s => s._key !== this.summary._key)
            this.onNew()
            this.snack.open("Summary has been deleted", "close")
        }catch(err) {
            console.error(err)
            this.snack.open("Error while deleting summary", "close")
        }
    }

    onExport() {
        this.downloadService.downloadFile(this.summary.htmlSummary, this.summary.name||"Summary report", this.format)
    }

    getMessage(s:ModelSummary) {
        if (this.isOutdated(s)) {
            return "May be out of date: the model has changed since it was written"
        }else return "Up to date with the model"
    }

    private isOutdated(s:ModelSummary) {
        return !!s?.created && s.created < this.linqset.model?.updatedDate
    }

    outdated() {
        return this.isOutdated(this.summary)
    }

    hasReport() {
        return (this.summary?.htmlSummary?.length || 0) > 0
    }

    // a prompt needs enough to go on before the AI is asked
    canGenerate() {
        return (this.summary?.prompt?.trim().length || 0) > 20
    }

    // anyone who can view can generate a summary, but saving and deleting need permission to change what it's saved with
    canChange() {
        return !this.linqset.readonly && this.linqset.project?.role !== USER_ROLE.READ
    }

    // saved against a model, the organisation or a saved filter, once it has a name and has been written
    canSave() {
        if (!this.canChange()) return false
        const where = this.linqset.type === LINQSET_TYPE.MODEL || this.linqset.type === LINQSET_TYPE.ORGANISATION || !!this.linqset.filter?._key
        return where && (this.summary?.name?.trim().length || 0) > 1 && (this.summary?.htmlSummary?.length || 0) > 5
    }

    promptHint() {
        const length = this.summary?.prompt?.trim().length || 0
        return length > 20 ? 'The model data is sent with the prompt.' : 'Write a little more to describe what you want (at least 20 characters).'
    }

    setDescription() {
        if (!this.canChange()) {
            this.description = "You can only view this, so you can generate a summary and export it, but not save it."
            return
        }
        if (this.linqset.type === LINQSET_TYPE.ORGANISATION) this.description = "Saved with the organisation."
        else if (this.linqset.type === LINQSET_TYPE.MODEL) this.description = `Saved with the model ${this.linqset.model.name}.`
        else if (this.linqset.filter._key) this.description = `Saved with the filter ${this.linqset.filter.name}.`
        else this.description = "A summary of several models can't be saved: choose one model, or the whole organisation, to save one."

        if (!this.summary?.name || this.summary.name.length === 0 || this.summary?.htmlSummary?.length === 0) {
            this.description += ' Name it and generate it to save it.'
        }

    }
}
